import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import StarRating from './StarRating';
import reviewService, { ReviewRequest, Review } from '../../services/reviewService';

interface ReviewFormData {
  title: string;
  comment: string;
  rating: number;
}

interface ReviewFormProps {
  open: boolean;
  onClose: () => void;
  productId: number;
  productTitle: string;
  onSuccess: (review: Review) => void;
}

// Validation schema
const validationSchema = Yup.object({
  rating: Yup.number()
    .min(1, 'Please select a rating')
    .max(5, 'Rating must be between 1 and 5')
    .required('Rating is required'),
  title: Yup.string()
    .trim()
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must not exceed 100 characters')
    .required('Title is required'),
  comment: Yup.string()
    .trim()
    .min(10, 'Comment must be at least 10 characters')
    .max(1000, 'Comment must not exceed 1000 characters')
    .required('Comment is required'),
});

const ReviewForm: React.FC<ReviewFormProps> = ({
  open,
  onClose,
  productId,
  productTitle,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialValues: ReviewFormData = {
    title: '',
    comment: '',
    rating: 0,
  };

  const handleSubmit = async (values: ReviewFormData, { setSubmitting, resetForm }: FormikHelpers<ReviewFormData>) => {
    setLoading(true);
    setError(null);

    const reviewData: ReviewRequest = {
      title: values.title.trim(),
      comment: values.comment.trim(),
      rating: values.rating
    };

    try {
      const review = await reviewService.createReview(productId, reviewData);
      onSuccess(review);
      resetForm();
      onClose();
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('reviews.errors.submitFailed'));
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('reviews.writeReview')}
        <Typography variant="subtitle2" color="text.secondary">
          {productTitle}
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('reviews.yourRating')}
                  </Typography>
                  <StarRating
                    rating={values.rating}
                    interactive
                    onChange={(newRating) => setFieldValue('rating', newRating)}
                    size="large"
                  />
                  {touched.rating && errors.rating && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.rating}
                    </Typography>
                  )}
                </Box>

                <Field
                  as={TextField}
                  fullWidth
                  name="title"
                  label={t('reviews.reviewTitle')}
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  margin="normal"
                  required
                  disabled={loading || isSubmitting}
                  inputProps={{ maxLength: 100 }}
                  helperText={
                    touched.title && errors.title
                      ? errors.title
                      : `${values.title.length}/100`
                  }
                  error={touched.title && Boolean(errors.title)}
                />

                <Field
                  as={TextField}
                  fullWidth
                  name="comment"
                  label={t('reviews.reviewComment')}
                  value={values.comment}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  margin="normal"
                  required
                  multiline
                  rows={4}
                  disabled={loading || isSubmitting}
                  inputProps={{ maxLength: 1000 }}
                  helperText={
                    touched.comment && errors.comment
                      ? errors.comment
                      : `${values.comment.length}/1000`
                  }
                  error={touched.comment && Boolean(errors.comment)}
                />

              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={loading || isSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || isSubmitting || values.rating === 0}
                startIcon={(loading || isSubmitting) && <CircularProgress size={20} />}
              >
                {t('reviews.submitReview')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ReviewForm;